import {
  type AddScheduleVisitRequest,
  type AddTemplateVisitsRequest,
  type AssignScheduleVisitsRequest,
  type DecideQualificationRequest,
  type DecideQualificationResponse,
  DecideQualificationResponseSchema,
  type DecisionPanelDto,
  DecisionPanelDtoSchema,
  type EnrollPatientRequest,
  type EnrollPatientResponse,
  EnrollPatientResponseSchema,
  type QualificationChecklistDto,
  QualificationChecklistDtoSchema,
  type SaveChecklistAnswerRequest,
  type SaveChecklistAnswerResponse,
  SaveChecklistAnswerResponseSchema,
  type OffsetPaginated,
  type PatientDto,
  PatientDtoSchema,
  PatientEnrollmentsDtoSchema,
  type PatientEnrollmentsDto,
  type PatientListQuery,
  type PatientVisitDto,
  PatientVisitDtoSchema,
  type RecordDecisionRequest,
  type RecordDecisionResponse,
  RecordDecisionResponseSchema,
  type RegisterPatientRequest,
  type RegisterPatientResponse,
  RegisterPatientResponseSchema,
  type ScheduleLogDto,
  ScheduleLogDtoSchema,
  type ScheduleVisitsDto,
  ScheduleVisitsDtoSchema,
  type TemplateVisitsDialogDto,
  TemplateVisitsDialogDtoSchema,
  type UnassignScheduleVisitsRequest,
  createOffsetPaginatedSchema,
} from '@ctms/contracts';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

const PatientListResponseSchema = createOffsetPaginatedSchema(PatientDtoSchema);

export const patientsApi = {
  async listPatients(query: PatientListQuery): Promise<OffsetPaginated<PatientDto>> {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(query.page));
    searchParams.set('limit', String(query.limit));
    if (query.search && query.search.trim().length > 0) {
      searchParams.set('search', query.search.trim());
    }
    if (query.sort) {
      searchParams.set('sort', query.sort);
      searchParams.set('order', query.order ?? 'asc');
    }
    const raw: unknown = await apiClient.get(API_PATHS.patients.list, { searchParams }).json();
    return PatientListResponseSchema.parse(raw);
  },

  async getPatient(id: number): Promise<PatientDto> {
    const raw: unknown = await apiClient.get(API_PATHS.patients.byId(id)).json();
    return PatientDtoSchema.parse(raw);
  },

  /**
   * Participation history of one chart, every participation with its own visit
   * schedule nested (elements 6 and 9 of the Cockpit legend). One round trip
   * covers both the chart bar and the visit table.
   */
  async listPatientEnrollments(id: number): Promise<PatientEnrollmentsDto> {
    const raw: unknown = await apiClient.get(API_PATHS.patients.enrollments(id)).json();
    return PatientEnrollmentsDtoSchema.parse(raw);
  },

  async registerPatient(request: RegisterPatientRequest): Promise<RegisterPatientResponse> {
    const raw: unknown = await apiClient.post(API_PATHS.patients.create, { json: request }).json();
    return RegisterPatientResponseSchema.parse(raw);
  },

  /**
   * Enrolls a patient into a clinical trial (REQ-PAT-009…014).
   * Generates the qualification-stage plan positions (REQ-PAT-025).
   */
  async enrollPatient(patientId: number, request: EnrollPatientRequest): Promise<EnrollPatientResponse> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.enrollments(patientId), { json: request })
      .json();
    return EnrollPatientResponseSchema.parse(raw);
  },

  // ─── Qualification dialog (issue #30; REQ-PAT-018…023, gate 4, R4) ───

  /**
   * Everything the qualification dialog renders in one round trip: the trial's
   * criteria already filtered by the person's sex (REQ-PAT-019), the saved
   * cells, the informational summary (R3) and the decision availability
   * (gate 1: only while the participation is `pre_screening`).
   */
  async getQualificationChecklist(
    patientId: number,
    enrollmentId: number,
  ): Promise<QualificationChecklistDto> {
    const raw: unknown = await apiClient
      .get(API_PATHS.patients.qualification(patientId, enrollmentId))
      .json();
    return QualificationChecklistDtoSchema.parse(raw);
  },

  /** Saves ONE checklist cell (gate 4) — re-saving the same cell replaces it. */
  async saveQualificationAnswer(
    patientId: number,
    enrollmentId: number,
    request: SaveChecklistAnswerRequest,
  ): Promise<SaveChecklistAnswerResponse> {
    const raw: unknown = await apiClient
      .put(API_PATHS.patients.qualificationAnswers(patientId, enrollmentId), { json: request })
      .json();
    return SaveChecklistAnswerResponseSchema.parse(raw);
  },

  /** The R4 decision: atomically swaps the status and persists the submitted checklist. */
  async decideQualification(
    patientId: number,
    enrollmentId: number,
    request: DecideQualificationRequest,
  ): Promise<DecideQualificationResponse> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.qualificationDecision(patientId, enrollmentId), { json: request })
      .json();
    return DecideQualificationResponseSchema.parse(raw);
  },

  // ─── Participation decisions panel (issue #29; REQ-PAT-014/032/033, Q-PAT-008) ───

  /**
   * Everything the panel renders in one round-trip: current status,
   * recordable decisions (Q-PAT-008 matrix — empty for terminal statuses),
   * and the immutable newest-first history with resolved actor names.
   */
  async getDecisionPanel(patientId: number, enrollmentId: number): Promise<DecisionPanelDto> {
    const raw: unknown = await apiClient
      .get(API_PATHS.patients.decisions(patientId, enrollmentId))
      .json();
    return DecisionPanelDtoSchema.parse(raw);
  },

  /**
   * Records one explicit clinician decision — atomic status toggle (or stage entry) +
   * history row. Never touches the visit schedule (2026-10-21 rescope).
   */
  async recordDecision(
    patientId: number,
    enrollmentId: number,
    request: RecordDecisionRequest,
  ): Promise<RecordDecisionResponse> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.decisions(patientId, enrollmentId), { json: request })
      .json();
    return RecordDecisionResponseSchema.parse(raw);
  },

  // ─── Manual schedule changes (issue #29 B6/B7; REQ-PAT-024/033) ───
  // Canonical DTOs from `@ctms/contracts` (`patients/schedule-log.schema.ts`);
  // the backend V3 endpoints are implemented (migration 0023).

  /**
   * The change log of one participation — append-only, newest-first
   * (kto / kiedy / dodano-usunięto + the catalog position, REQ-PAT-033).
   */
  async getScheduleLog(patientId: number, enrollmentId: number): Promise<ScheduleLogDto> {
    const raw: unknown = await apiClient
      .get(API_PATHS.patients.schedule(patientId, enrollmentId))
      .json();
    return ScheduleLogDtoSchema.parse(raw);
  },

  /**
   * B6: manually adds a catalog position to the schedule — the visit is
   * created WITHOUT a booked day (booking stays with „Umawiaj”). Possible
   * regardless of the treatment stage and recorded decisions (frozen spec
   * section B). The backend writes the `added` log row in the same transaction.
   */
  async addScheduleVisit(
    patientId: number,
    enrollmentId: number,
    request: AddScheduleVisitRequest,
  ): Promise<PatientVisitDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.schedule(patientId, enrollmentId), { json: request })
      .json();
    return PatientVisitDtoSchema.parse(raw);
  },

  /**
   * B7: manually removes a scheduled, NOT performed visit (hard delete is
   * allowed — no GCP rigor; the backend rejects a performed visit with 409).
   * The backend writes the `removed` log row in the same transaction; the log
   * row outlives the deleted visit.
   */
  async removeScheduleVisit(
    patientId: number,
    enrollmentId: number,
    visitId: number,
  ): Promise<PatientVisitDto> {
    const raw: unknown = await apiClient
      .delete(API_PATHS.patients.scheduleVisit(patientId, enrollmentId, visitId))
      .json();
    return PatientVisitDtoSchema.parse(raw);
  },

  /**
   * #39 (T5): bulk-assigns catalog positions to the schedule — each position
   * at most once per participation; a duplicate (also inside one request) is
   * a 409 and NOTHING is written (ALL-OR-NOTHING: visits + `added` log rows
   * in one transaction). The visits are created WITHOUT a booked day.
   */
  async assignScheduleVisits(
    patientId: number,
    enrollmentId: number,
    request: AssignScheduleVisitsRequest,
  ): Promise<ScheduleVisitsDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.scheduleAssign(patientId, enrollmentId), { json: request })
      .json();
    return ScheduleVisitsDtoSchema.parse(raw);
  },

  /**
   * #39 (B7 bulk): removes several scheduled, NOT performed visits in one
   * ALL-OR-NOTHING step — a performed (409) or unknown (404) visit rejects
   * the whole batch. The `removed` log rows outlive the deleted visits.
   */
  async unassignScheduleVisits(
    patientId: number,
    enrollmentId: number,
    request: UnassignScheduleVisitsRequest,
  ): Promise<ScheduleVisitsDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.scheduleUnassign(patientId, enrollmentId), { json: request })
      .json();
    return ScheduleVisitsDtoSchema.parse(raw);
  },

  // ─── Template visits (issue #41) ───
  // Canonical DTOs from `@ctms/contracts` (`patients/template-visits.schema.ts`);
  // the backend doors live in `schedule.controller.ts` (GET = patients:read,
  // POST = patients:write, all-or-nothing bulk).

  /**
   * The „Wizyty szablonowe" dialog state — the CONFIGURED preset set
   * (names/codes/channels/descriptions are server-side configuration, GO
   * conclusion 3) with the per-enrollment `alreadyAdded` probe and the
   * categorical `repeatable` flag (#43: UNS repeats, OUT is one-time).
   */
  async getTemplateVisitsDialog(
    patientId: number,
    enrollmentId: number,
  ): Promise<TemplateVisitsDialogDto> {
    const raw: unknown = await apiClient
      .get(API_PATHS.patients.scheduleTemplateVisits(patientId, enrollmentId))
      .json();
    return TemplateVisitsDialogDtoSchema.parse(raw);
  },

  /**
   * „DODAJ WIZYTY": adds the selected presets in ONE all-or-nothing bulk —
   * a duplicate of a ONE-TIME preset (category OUT) rejects the ENTIRE
   * operation with 409 TEMPLATE_VISIT_DUPLICATE. The response carries the
   * created visits in REQUEST order (`presetCode` stays DB-internal).
   */
  async addTemplateVisits(
    patientId: number,
    enrollmentId: number,
    request: AddTemplateVisitsRequest,
  ): Promise<ScheduleVisitsDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.patients.scheduleTemplateVisits(patientId, enrollmentId), { json: request })
      .json();
    return ScheduleVisitsDtoSchema.parse(raw);
  },
};
